export type Child = Node | string | number | null | undefined | false;
export type Attrs = Record<string, unknown>;

/** Minimaler DOM-Builder: h('div', {class: 'x', onclick: fn}, kinder…) */
export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  ...children: (Child | Child[])[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === null || v === false) continue;
    if (k.startsWith('on') && typeof v === 'function') {
      el.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
    } else if (k === 'class') {
      el.className = String(v);
    } else if (k === 'value') {
      (el as unknown as HTMLInputElement).value = String(v);
    } else if (v === true) {
      el.setAttribute(k, '');
    } else {
      el.setAttribute(k, String(v));
    }
  }
  for (const c of children.flat()) {
    if (c === null || c === undefined || c === false) continue;
    el.append(typeof c === 'number' ? String(c) : c);
  }
  return el;
}

// --- Icons -------------------------------------------------------------------
const ICONS = {
  calendar: '<rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>',
  list: '<path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/>',
  upload: '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M17 8l-5-5-5 5M12 3v12"/>',
  export: '<path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/>',
  settings: '<path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6"/>',
  key: '<circle cx="7.5" cy="15.5" r="4.5"/><path d="M10.7 12.3L21 2M16 7l3 3M18 5l2 2"/>',
  logout: '<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="M16 17l5-5-5-5M21 12H9"/>',
  menu: '<path d="M3 6h18M3 12h18M3 18h18"/>',
  plus: '<path d="M12 5v14M5 12h14"/>',
  trash:
    '<path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/>',
  close: '<path d="M18 6L6 18M6 6l12 12"/>',
} as const;

export type IconName = keyof typeof ICONS;

export function icon(name: IconName, size = 20): HTMLSpanElement {
  const span = h('span', { class: 'icon' });
  span.innerHTML = `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${ICONS[name]}</svg>`;
  return span;
}

// --- Dialoge -----------------------------------------------------------------
export interface DialogHandle {
  el: HTMLDialogElement;
  body: HTMLElement;
  close(): void;
}

export function openDialog(title: string): DialogHandle {
  const body = h('div', { class: 'dlg-body' });
  const closeBtn = h(
    'button',
    { class: 'icon-btn', type: 'button', title: 'Schließen', 'aria-label': 'Schließen' },
    icon('close', 18),
  );
  const el = h('dialog', { class: 'dlg' }, h('div', { class: 'dlg-head' }, h('h2', {}, title), closeBtn), body);
  const handle: DialogHandle = { el, body, close: () => el.close() };
  closeBtn.addEventListener('click', handle.close);
  el.addEventListener('close', () => el.remove());
  document.body.append(el);
  el.showModal();
  return handle;
}

export function focusFirst(dlg: DialogHandle): void {
  dlg.body.querySelector<HTMLElement>('input:not([type=file]):not([type=checkbox]), select, textarea')?.focus();
}

export function confirmDialog(message: string, okLabel = 'Löschen', danger = true): Promise<boolean> {
  return new Promise((resolve) => {
    const d = openDialog('Bitte bestätigen');
    let result = false;
    d.el.addEventListener('close', () => resolve(result));
    d.body.append(
      h('p', {}, message),
      h(
        'div',
        { class: 'actions' },
        h('button', { type: 'button', class: 'btn', onclick: () => d.close() }, 'Abbrechen'),
        h(
          'button',
          {
            type: 'button',
            class: danger ? 'btn btn-danger' : 'btn btn-primary',
            onclick: () => {
              result = true;
              d.close();
            },
          },
          okLabel,
        ),
      ),
    );
  });
}

export function field(label: string, input: HTMLElement, hint?: string): HTMLElement {
  const id = input.id || `f-${Math.random().toString(36).slice(2, 9)}`;
  input.id = id;
  return h(
    'div',
    { class: 'field' },
    h('label', { for: id }, label),
    input,
    hint ? h('div', { class: 'hint' }, hint) : null,
  );
}

export function toast(msg: string, kind: 'ok' | 'error' = 'ok'): void {
  const box = document.getElementById('toasts');
  if (!box) return;
  const t = h('div', { class: `toast ${kind}`, role: kind === 'error' ? 'alert' : 'status' }, msg);
  box.append(t);
  setTimeout(() => t.remove(), kind === 'error' ? 6000 : 3000);
}
